import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  FiUpload, 
  FiTrash2, 
  FiEdit2, 
  FiX,
  FiImage,
  FiPlus
} from 'react-icons/fi';
import api, { getImageUrl } from '../../services/api';
import { useContent } from '../../context/ContentContext';
import './AdminGallery.css';

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const fileInputRef = useRef(null);
  const { content } = useContent();

  // Dinamik kategoriler
  const categories = content.gallery?.categories || [];

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      // Admin panelinde tüm resimleri görmek istiyoruz, bu yüzden limiti yüksek tutuyoruz
      const response = await api.get('/upload/gallery', { 
        params: { limit: 1000 } 
      });
      
      // Backend artık { images: [...], total: ... } dönüyor
      // Eğer images array'i yoksa (eski yapı veya hata) boş array kullan
      const galleryImages = response.data.images || [];
      setImages(galleryImages);
    } catch (error) {
      console.error('Galeri yüklenemedi:', error);
      toast.error('Galeri yüklenemedi. Sunucu bağlantısını kontrol edin.');
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    
    let successCount = 0;
    let errorCount = 0;

    // Her dosya için ayrı ayrı upload yap
    for (let i = 0; i < files.length; i++) {
      const formData = new FormData();
      formData.append('image', files[i]);
      formData.append('title', files[i].name.replace(/\.[^/.]+$/, '') || 'Yeni Resim'); // Dosya adını başlık yap
      formData.append('category', categories[0]?.id || 'general');

      try {
        await api.post('/upload/gallery', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch (error) {
        console.error(`Dosya yüklenemedi: ${files[i].name}`, error);
        errorCount++;
      }
    }

    // Sonuç bildirimi
    if (successCount > 0 && errorCount === 0) {
      toast.success(`${successCount} resim başarıyla yüklendi`);
    } else if (successCount > 0 && errorCount > 0) {
      toast.success(`${successCount} resim yüklendi, ${errorCount} resim başarısız`);
    } else {
      toast.error('Yükleme başarısız');
    }

    fetchGallery();
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu resmi silmek istediğinize emin misiniz?')) return;

    try {
      await api.delete(`/upload/gallery/${id}`);
      toast.success('Resim silindi');
      setImages(images.filter(img => img._id !== id));
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const handleUpdate = async () => {
    if (!editingImage) return;

    try {
      await api.put(`/upload/gallery/${editingImage._id}`, {
        title: editingImage.title,
        description: editingImage.description,
        category: editingImage.category
      });
      toast.success('Resim güncellendi');
      fetchGallery();
      setEditingImage(null);
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  return (
    <div className="admin-gallery">
      {/* Upload Section */}
      <div className="upload-section">
        <h3>Yeni Resim Yükle</h3>
        <div 
          className={`upload-area ${uploading ? 'uploading' : ''}`}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? (
            <>
              <div className="spinner"></div>
              <span>Yükleniyor...</span>
            </>
          ) : (
            <>
              <FiUpload />
              <span>Resim yüklemek için tıklayın veya sürükleyin</span>
              <small>PNG, JPG, GIF - Maksimum 5MB</small>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleUpload}
          style={{ display: 'none' }}
        />
      </div>

      {/* Gallery Grid */}
      <div className="gallery-section">
        <h3>Galeri ({images.length} Resim)</h3>
        
        {loading ? (
          <div className="gallery-loading">
            <div className="spinner"></div>
            <p>Yükleniyor...</p>
          </div>
        ) : images.length === 0 ? (
          <div className="gallery-empty">
            <FiImage />
            <p>Henüz resim yüklenmemiş</p>
            <button 
              className="btn btn-primary"
              onClick={() => fileInputRef.current?.click()}
            >
              <FiPlus />
              İlk Resmi Yükle
            </button>
          </div>
        ) : (
          <div className="gallery-grid">
            {images.map((image, index) => (
              <motion.div
                key={image._id}
                className="gallery-item"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
              >
                {image.imageUrl ? (
                  <img src={getImageUrl(image.imageUrl)} alt={image.title} />
                ) : (
                  <div className="image-placeholder">
                    <FiImage />
                  </div>
                )}
                <div className="item-overlay">
                  <span className="item-title">{image.title}</span>
                  <span className="item-category">
                    {categories.find(c => c.id === image.category)?.label || image.category}
                  </span>
                </div>
                <div className="item-actions">
                  <button 
                    className="action-btn edit"
                    onClick={() => setEditingImage(image)}
                  >
                    <FiEdit2 />
                  </button>
                  <button 
                    className="action-btn delete"
                    onClick={() => handleDelete(image._id)}
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editingImage && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setEditingImage(null)}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Resim Düzenle</h2>
                <button onClick={() => setEditingImage(null)}><FiX /></button>
              </div>
              
              <div className="modal-body">
                <div className="edit-preview">
                  {editingImage.imageUrl ? (
                    <img src={getImageUrl(editingImage.imageUrl)} alt={editingImage.title} />
                  ) : (
                    <div className="preview-placeholder">
                      <FiImage />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Başlık</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editingImage.title}
                    onChange={(e) => setEditingImage({
                      ...editingImage,
                      title: e.target.value
                    })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Açıklama</label>
                  <textarea
                    className="form-textarea"
                    rows={3}
                    value={editingImage.description || ''}
                    onChange={(e) => setEditingImage({
                      ...editingImage,
                      description: e.target.value
                    })}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Kategori</label>
                  <select
                    className="form-select"
                    value={editingImage.category}
                    onChange={(e) => setEditingImage({
                      ...editingImage,
                      category: e.target.value
                    })}
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setEditingImage(null)}
                >
                  İptal
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleUpdate}
                >
                  Kaydet
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminGallery;

